import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Loader, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { createLogger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';

const logger = createLogger('promptversus');

interface AIResponse {
  model: string;
  response: string;
  responseTime?: string;
}

interface PromptHistory {
  id: string;
  prompt: string;
  summary: string;
  timestamp: string;
  results: AIResponse[];
}

const PromptversusPage: React.FC = () => {
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<AIResponse[]>([]);
  const [history, setHistory] = useState<PromptHistory[]>([]);
  const [showHistory, setShowHistory] = useState(true);
  const [cost, setCost] = useState<{actualCost: number, markup: string, finalCost: number} | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Function to generate a concise summary from a prompt
  const generateSummary = (prompt: string): string => {
    // Split into words and get first 7 meaningful words
    const words = prompt.split(/\s+/)
      .filter(word => word.length > 2) // Filter out small words
      .slice(0, 7)
      .join(' ');
    
    // Add ellipsis if truncated
    return words + (prompt.split(/\s+/).length > 7 ? '...' : '');
  };

  useEffect(() => {
    // Load history from localStorage on component mount
    const loadHistory = () => {
      const savedHistory = localStorage.getItem(`promptversus_history_${user?.id}`);
      if (savedHistory) {
        try {
          const parsedHistory = JSON.parse(savedHistory);
          // Only keep history items from the last 60 days
          const sixtyDaysAgo = new Date();
          sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
          const filteredHistory = parsedHistory.filter((item: PromptHistory) => 
            new Date(item.timestamp) > sixtyDaysAgo
          );
          setHistory(filteredHistory);
        } catch (e) {
          console.error('Error parsing history:', e);
        }
      }
    };

    loadHistory();
  }, [user?.id]);

  const saveToHistory = (newPrompt: string, newResults: AIResponse[]) => {
    const historyItem: PromptHistory = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2)}`,
      prompt: newPrompt,
      summary: generateSummary(newPrompt),
      timestamp: new Date().toISOString(),
      results: newResults
    };

    const updatedHistory = [historyItem, ...history];
    setHistory(updatedHistory);

    // Save to localStorage
    localStorage.setItem(
      `promptversus_history_${user?.id}`,
      JSON.stringify(updatedHistory)
    );
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Please enter a prompt or question');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Generate a unique instance ID for tracking
      const instanceId = `promptversus-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      logger.info('Sending prompt to Promptversus API', { 
        prompt, 
        instanceId,
        userId: user?.id 
      });

      const apiUrl = config.apiUrl;
      const response = await fetch(`${apiUrl}/api/mindstudio/promptversus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
        },
        body: JSON.stringify({
          prompt,
          instanceId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process request');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to process request');
      }

      // Store cost information if available
      if (data.cost) {
        setCost(data.cost);
      }

      const resultData = data.results?.result || {};
      let parsedResult = resultData;
      if (typeof resultData === 'string') {
        try {
          parsedResult = JSON.parse(resultData);
        } catch (e) {
          console.error('Failed to parse result as JSON:', e);
        }
      }

      // Format the results with correct model names and include all responses
      const formattedResults: AIResponse[] = [
        { model: "GPT-4o", response: parsedResult.reschatgpt || "No response" },
        { model: "Claude 3.5 Sonnet", response: parsedResult.resclaude || "No response" },
        { model: "Perplexity", response: parsedResult.resperplexity || "No response" },
        { model: "Amazon Nova Pro", response: parsedResult.resamazon || "No response" },
        { model: "Gemini 2.0 Pro", response: parsedResult.resgemini || "No response" },
        { model: "Llama 3 70B", response: parsedResult.resllama || "No response" },
        { model: "Mistral Large", response: parsedResult.resmistral || "No response" }
      ];

      setResults(formattedResults);
      saveToHistory(prompt, formattedResults);
      setStep(3);

    } catch (error) {
      logger.error('Error calling Promptversus API', error);
      setError(error instanceof Error ? error.message : 'Failed to process your request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistoryItem = (item: PromptHistory) => {
    setPrompt(item.prompt);
    setResults(item.results);
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="p-2 mr-4 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Promptversus</h1>
          </div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            title={showHistory ? "Hide History" : "Show History"}
          >
            <History className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-6">
          {/* History Sidebar */}
          {showHistory && (
            <div className="w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">History</h2>
                <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {history.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => loadHistoryItem(item)}
                      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.summary}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(item.timestamp).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                  {history.length === 0 && (
                    <p className="text-sm text-gray-500 text-center">No history yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {step === 1 && (
                <div className="p-6">
                  <img 
                    src="/assets/Promptversus1.webp" 
                    alt="Promptversus Banner" 
                    className="w-full h-auto rounded-lg mb-6"
                  />
                  <div className="prose max-w-none">
                    <h2 className="text-xl font-bold text-center mb-6">Introducing Promptversus: The Ultimate LLM Showdown</h2>
                    
                    <p>
                      Welcome to <strong>Promptversus</strong>, where top-tier language models face off to deliver the most accurate, insightful, and consistent answers. Designed for professionals who demand precision and reliability, Promptversus puts the power of AI at your fingertips, comparing responses from the seven leading LLMs under identical conditions—Temperature of 0.5 and up to 2,000 tokens.
                    </p>
                    
                    <h3 className="text-lg font-semibold mt-6 mb-3">Key Features of Promptversus:</h3>
                    
                    <ul>
                      <li>
                        <strong>Head-to-Head LLM Comparison:</strong> Promptversus simultaneously queries seven top-performing LLMs, allowing you to assess the consistency and accuracy of responses to ensure trustworthy insights.
                      </li>
                      <li>
                        <strong>Rigorous Consistency Testing:</strong> By setting a uniform temperature and token limit, Promptversus reveals subtle differences or alignments in LLM responses, giving you a clear view of model reliability.
                      </li>
                      <li>
                        <strong>Focused on Factual Accuracy:</strong> Promptversus helps you fact-check critical information—from exchange rates to regulatory benchmarks—enhancing confidence in decisions that depend on precise data.
                      </li>
                      <li>
                        <strong>Real-Time Performance Insights:</strong> Watch as each model competes to answer your prompt, providing clarity on each LLM's strengths, coverage, and areas of distinction.
                      </li>
                      <li>
                        <strong>Professional Precision:</strong> Tailored for auditors, analysts, and finance professionals, Promptversus is built to support high-stakes work, empowering you with a robust tool for fact verification and comparative analysis.
                      </li>
                    </ul>
                    
                    <p className="mt-6">
                      <strong>Promptversus</strong> transforms how you leverage AI for accuracy. Dive into a world where top LLMs compete, delivering the highest standards in fact-checking and analytical power. With Promptversus, you're equipped to harness the best of AI, ensuring every answer stands up to scrutiny. Experience AI-driven assurance, reimagined.
                    </p>
                  </div>
                  
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={handleNext}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
                    >
                      <span>Next</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="p-6">
                  <img 
                    src="/assets/Promptversus2.png" 
                    alt="Promptversus Comparison" 
                    className="w-64 h-64 mx-auto mb-6"
                  />
                  <h2 className="text-xl font-bold text-center mb-6">What is your prompt / question that will be posed to all 7 AI Assistants?</h2>
                  
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Enter your prompt or question here..."
                      disabled={isLoading}
                    ></textarea>
                    
                    <div className="mt-6 flex justify-center">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <Loader className="h-5 w-5 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <span>Submit</span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {step === 3 && (
                <div className="p-6">
                  <img 
                    src="/assets/Promptversus2.png" 
                    alt="Promptversus Comparison" 
                    className="w-64 h-64 mx-auto mb-6"
                  />
                  <h2 className="text-xl font-bold text-center mb-6">Results Comparison</h2>
                  
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Your prompt:</h3>
                    <p className="text-gray-600">{prompt}</p>
                  </div>
                  
                  {cost && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-medium text-blue-700 mb-2">Usage Information:</h3>
                      <p className="text-blue-600">
                        Actual API cost: ${cost.actualCost.toFixed(4)} | 
                        Markup: {cost.markup} | 
                        Total cost: ${cost.finalCost.toFixed(4)}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-8">
                    {results.map((result, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="font-medium text-gray-900">{result.model}</h3>
                        </div>
                        <div className="p-4">
                          <div className="prose max-w-none">
                            <ReactMarkdown>{result.response}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => setStep(2)}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
                    >
                      <span>Ask Another Question</span>
                    </button>
                  </div>
                </div>
              )}

              {isLoading && step === 2 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
                    <div className="flex flex-col items-center">
                      <Loader className="h-12 w-12 text-cyan-600 animate-spin mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 mb-2">Processing your request...</h3>
                      <p className="text-gray-500 text-center">
                        Please wait while we compare AI responses from all seven language models.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptversusPage;